import { useState, useEffect, useContext } from 'react';
// Removed useSettingsI18n - using native Paraglide JS
import { 
  common_loading,
  settings_payment_redirecting,
  settings_payment_redirect_failed,
  settings_required_authentication,
  settings_logout_success,
  settings_logout_error,
  settings_logout_vscode,
  settings_logout_vscodeError,
  settings_email_notReady as settings_email_notready,
  settings_phone_notReady as settings_phone_notready,
  settings_debug_initializing,
  settings_debug_fetchingEmail,
  settings_debug_emailFetched,
  settings_debug_emailFetchFailed,
  settings_debug_emailFetchError,
  settings_debug_logoutError,
  settings_debug_paymentRedirect,
  settings_debug_paymentRedirectError,
  settings_debug_vscodeLogout,
  settings_debug_vscodeNoMessenger
} from '@paraglide/messages';
import { useParams } from 'react-router';
import { useI18nNavigate } from '../../../../src/hooks/useI18nNavigate';
// Lucide React 직접 import - Vite 번들링 최적화 및 초기화 순서 문제 해결
import Loader2 from 'lucide-react/dist/esm/icons/loader-2';
import toast from 'react-hot-toast';
import { 
  SettingsPage as UISettingsPage, 
  SettingsSection, 
  SettingItem 
} from '@edusense/features-settings';
import { useAuth } from '@edusense/shared-auth-core';
import { isVSCodeEnvironment } from '@edusense/navigation-utils';
import { IdeMessengerContext } from '@edusense/features-ide-messenger';
import { createSettingsSections } from './settingsConfig';

const SettingsPageWrapper = () => {
  // Paraglide migration completed - using native Paraglide JS functions
  
  const navigate = useI18nNavigate(); // 🌟 커스텀 i18n navigate 사용
  const params = useParams();
  const currentLang = params.lang || 'ko'; // React Router v7 언어 파라미터
  const { user, logout, isInitialized, isAuthenticated, fetchUserEmail, token: accessToken } = useAuth();
  const ideMessenger = useContext(IdeMessengerContext);
  
  const [emailFetchAttempted, setEmailFetchAttempted] = useState(false);

  useEffect(() => {
    if (isAuthenticated && user && !user.email && fetchUserEmail && !emailFetchAttempted) {
      console.log(settings_debug_fetchingEmail());
      setEmailFetchAttempted(true);
      fetchUserEmail().then(fetchedEmail => {
        if (fetchedEmail) {
          console.log(settings_debug_emailFetched(), fetchedEmail);
        } else {
          console.warn(settings_debug_emailFetchFailed());
        }
      }).catch(error => {
        console.error(settings_debug_emailFetchError(), error);
        setEmailFetchAttempted(false);
      });
    }
  }, [isAuthenticated, user, fetchUserEmail, emailFetchAttempted]);

  const handleLogout = async () => {
    const isVSCode = isVSCodeEnvironment();
    if (isVSCode) {
      console.log(settings_debug_vscodeLogout());
      if (ideMessenger) {
        ideMessenger.post("edusenseLogout", undefined);
        toast(settings_logout_vscode());
      } else {
        console.error(settings_debug_vscodeNoMessenger());
        toast.error(settings_logout_vscodeError());
      }
    } else {
      try {
        await logout();
        toast.success(settings_logout_success());
        navigate('/landing'); // useI18nNavigate가 자동으로 /ko/landing으로 변환
      } catch (error) {
        console.error(settings_debug_logoutError(), error);
        toast.error(settings_logout_error());
      }
    }
  };

  const handleEditEmail = () => {
    toast(settings_email_notready());
  };

  const handleEditPhone = () => {
    toast(settings_phone_notready());
  };

  const navigateToPaymentPageWithAuth = async (path: string) => {
    if (!isAuthenticated || !accessToken || !user?.id) {
      toast.error(settings_required_authentication());
      return false;
    }
    
    try {
      const payUrl = import.meta.env.VITE_PAY_URL || 'http://localhost:8777';
      const targetUrl = `${payUrl}${path}`;
      
      console.log(settings_debug_paymentRedirect(), targetUrl);
      
      window.location.href = targetUrl;
      
      toast.success(settings_payment_redirecting(), { duration: 1000 });
      return true;

    } catch (error) {
      console.error(settings_debug_paymentRedirectError(), error);
      toast.error(settings_payment_redirect_failed());
      return false;
    }
  };

  const handleItemClick = async (item: SettingItem) => {
    if (item.path) {
      // 결제 관련 경로들은 세션 생성 후 이동
      if (item.path.startsWith('/payment/') || item.path.startsWith('/subscribe/') || item.path.startsWith('/subscription/')) {
        await navigateToPaymentPageWithAuth(item.path);
      } else {
        // useI18nNavigate가 자동으로 언어 prefix 추가
        navigate(item.path);
      }
    }
  };

  const handleToggleChange = (itemId: string, isActive: boolean) => {
    // 현재 구현에서는 토글 설정이 없으므로 빈 처리
    console.log('Toggle changed:', itemId, isActive);
  };

  const handleEditProfile = () => {
    navigate('/profile/edit');
  };

  // 디버깅 로그
  console.log('[SettingsPageWrapper] Auth state:', { isInitialized, isAuthenticated, user: !!user });

  if (!isInitialized) {
    console.log(settings_debug_initializing(), { isInitialized, isAuthenticated, user });
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        <div className="mt-2 text-sm text-gray-600">{common_loading()}</div>
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    // 현재 언어 경로 유지
    const currentPath = window.location.pathname;
    const pathSegments = currentPath.split('/');
    const currentLang = pathSegments[1];
    
    console.log('[SettingsPageWrapper] Redirecting to login, current path:', currentPath, 'lang:', currentLang);
    
    if (currentLang && ['ko', 'en', 'ja'].includes(currentLang)) {
      // 절대 경로 유지 (로그인 페이지로 가는 경우)
      window.location.href = `/login?redirect=/${currentLang}/settings`;
    } else {
      window.location.href = '/login?redirect=/settings';
    }
    return null;
  }

  // Settings sections configuration extracted to separate file for maintainability
  const sections = createSettingsSections(navigate);

  // user 객체를 ui-layout의 SettingsUser 타입에 맞게 변환
  const settingsUser = user ? {
    id: user.id,
    name: user.name || user.username,
    username: user.username,
    email: user.email,
    profileImage: user.profileImage || user.avatar,
    phone: user.phone
  } : {
    id: 'guest',
    name: '게스트',
    username: 'guest',
    email: 'guest@example.com'
  };

  return (
    <UISettingsPage
      user={settingsUser}
      sections={sections}
      onItemClick={handleItemClick}
      onToggleChange={handleToggleChange}
      onEditProfile={handleEditProfile}
      onEditEmail={handleEditEmail}
      onEditPhone={handleEditPhone}
      onLogout={handleLogout}
    />
  );
};

export default SettingsPageWrapper;